'use client'

import { Chip, Flex, Group, Text } from '@mantine/core'
import { useParams } from 'next/navigation'
import { useTranslations } from 'next-intl'

import { useUpdateSearchParams } from '@/hooks/routing'
import { tokenizedStocksLocales } from '@/i18n/locales'
import { useI18nRouter } from '@/i18n/navigation'

import {
  COINS_OG_PAGE_SIZE,
  COINS_PAGE_SIZE,
  CoinTab,
  defaultCoinTab,
  getAllAssetsTab,
  getCoinTabs,
  getTokenizedStocksTab,
} from '../../configs'
import { useCoinsTableStore } from '../../context'
import styles from './styles.module.css'

export default function CoinTabs({
  isOGPrice,
  tabVariant,
}: {
  isOGPrice?: boolean
  tabVariant?: 'tokenizedStocks'
}) {
  const pageSize = isOGPrice ? COINS_OG_PAGE_SIZE : COINS_PAGE_SIZE
  const { locale } = useParams<{ locale: string }>()
  const router = useI18nRouter()

  const isTokenizedStocksPage = tabVariant === 'tokenizedStocks'
  const isTokenizedStocksLocale = (tokenizedStocksLocales as string[]).includes(locale)

  let coinTabs = getCoinTabs(pageSize)
  if (isOGPrice) coinTabs = coinTabs.filter(tab => tab.value !== CoinTab.Favorites)

  let displayedTabs = coinTabs
  if (isTokenizedStocksPage) {
    const tokenizedStocksTabOrder = [
      CoinTab.Favorites,
      CoinTab.AllCoins,
      CoinTab.TokenizedStocks,
      CoinTab.TopGainers,
      CoinTab.TopLosers,
      CoinTab.New,
      CoinTab.MostPopular,
    ]
    const allTabs = [...coinTabs, getTokenizedStocksTab()]
    displayedTabs = tokenizedStocksTabOrder
      .map(value => allTabs.find(tab => tab.value === value))
      .filter(Boolean) as typeof coinTabs
  } else if (isTokenizedStocksLocale) {
    const tabs = [
      getAllAssetsTab(pageSize),
      coinTabs[0],
      getTokenizedStocksTab(),
      ...coinTabs.slice(1),
    ]
    displayedTabs = tabs.filter(tab => tab.value !== CoinTab.MostPopular)
  }

  const currentCoinTab = useCoinsTableStore(state => state.currentCoinTab)
  const updateFilters = useCoinsTableStore(state => state.updateFilters)

  const { updateSearchParams } = useUpdateSearchParams(true, false, true)

  const t = useTranslations('common.coinCategoryTypes')

  const handleTabClick = (tabValue: string) => {
    if (tabValue === CoinTab.TokenizedStocks && !isTokenizedStocksPage) {
      router.push('/price/tokenized-stocks')
      return
    }

    const newCurrentCoinTab = displayedTabs.find(tab => tab.value === tabValue) ?? defaultCoinTab
    updateFilters({
      currentCoinTab: newCurrentCoinTab,
      currentPage: 1,
    })

    const defaultTabValue = isTokenizedStocksPage
      ? CoinTab.TokenizedStocks
      : isTokenizedStocksLocale
        ? CoinTab.AllAssets
        : defaultCoinTab.value
    updateSearchParams([
      { name: 'tab', value: tabValue, deleteParam: tabValue === defaultTabValue },
      { name: 'page', deleteParam: true },
    ])
  }

  return (
    <Flex className={styles.container}>
      <Chip.Group multiple={false} value={currentCoinTab.value} onChange={handleTabClick}>
        <Group className={styles.tabs}>
          {displayedTabs.map(tab => (
            <Chip
              key={tab.value}
              icon={null}
              classNames={styles}
              variant="outline"
              value={tab.value}
              onClick={() => handleTabClick(tab.value)}
            >
              <Text variant="subtitle-emphasis">
                {isTokenizedStocksLocale && tab.value === CoinTab.AllCoins
                  ? t('crypto')
                  : t(tab.labelTranslationKey)}
              </Text>
            </Chip>
          ))}
        </Group>
      </Chip.Group>
    </Flex>
  )
}
